import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import EmptyIcon from '@/assets/icons/empty.svg';
import Spinner from '@/components/atoms/Spinner';
import SideFilter from '@/components/molecules/SideFilter';
import { TrainingFileTeamCard } from '@/components/organisms/TrainingFileTeamCard';
import { Button } from '@/components/ui/button';
import { trainingFileStatus } from '@/constants/trainingFileStatus';
import api from '@/services/apiSgft';
import { trainingFilesDetailsFiltersAtom } from '@/state/TrainingFilesDetailsFilter.atom';
import { Filter } from '@/types/Filter';
import { TrainingFileDetails } from '@/types/TrainingFile';

const TrainingFileTeams = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const filterValues = useRecoilValue(trainingFilesDetailsFiltersAtom);
	const [filteredData, setFilteredData] = useState(filterValues);
	const paramTeam = searchParams.get('turma');
	const { id: trainingFileId } = useParams();

	const getTrainingPlanningScope = (teams?: string[]) =>
		teams?.length ? undefined : paramTeam || undefined;

	const fetchDetails = async () => {
		const params = {
			startDate: filteredData.timeRange.startDate,
			endDate: filteredData.timeRange.endDate,
			employees: filteredData.employee,
			managements: filteredData.management,
			teams: filteredData.team,
			trainingPlanningId: getTrainingPlanningScope(filteredData.team),
			areaCoordinators: filteredData.areaCoordinator,
			roles: filteredData.role,
			workStations: filteredData.workStation,
		};
		const trainingPlanningDetails = api.get(
			`training-plannings/details/${trainingFileId}`,
			{
				params,
			},
		);

		const trainingFile = api.get(`training-files/${trainingFileId}`);

		const [trainingPlanningDetailsResponse, trainingFileResponse] =
			await Promise.all([trainingPlanningDetails, trainingFile]);

		return {
			...trainingPlanningDetailsResponse.data,
			trainingFile: trainingFileResponse.data,
		};
	};

	const { data, isLoading } = useQuery<
		TrainingFileDetails & { trainingFile: { status: string } }
	>(
		['training-file-details', paramTeam, trainingFileId, filteredData],
		fetchDetails,
	);
	const handleApplyFilter = (isReseting: boolean) => {
		if (isReseting) {
			setFilteredData(() => ({
				team: [],
				timeRange: {
					endDate: undefined,
					startDate: undefined,
				},
				employee: [],
				management: [],
				areaCoordinator: [],
				role: [],
				workStation: [],
			}));
		} else {
			setFilteredData(() => ({
				...filterValues,
			}));
		}
	};
	const fetchFilter = async (
		field: string,
		search: string,
		atom?: typeof filterValues,
	) => {
		const response = await api.get(`${field}/filter`, {
			params: {
				trainingFileId: Number(trainingFileId),
				trainingPlanningId: getTrainingPlanningScope(atom?.team),
				search,
				size: 10,
				page: 1,
				teams: atom?.team,
				managements: atom?.management,
				areaCoordinators: atom?.areaCoordinator,
				roles: atom?.role,
				workStations: atom?.workStation,
				employees: atom?.employee,
			},
		});
		const options = response.data.map((it: { name: string; id: string }) => ({
			label: it.name,
			value: String(it.id),
		}));
		return options;
	};

	const fetchTeamFilter = async (
		search: string,
		atom?: typeof filterValues,
	) => {
		const response = await api.get(`training-plannings/teams/filter`, {
			params: {
				trainingFileId: Number(trainingFileId),
				trainingPlanningId: getTrainingPlanningScope(atom?.team),
				search,
				size: 10,
				page: 1,
				teams: atom?.team,
				managements: atom?.management,
				areaCoordinators: atom?.areaCoordinator,
				roles: atom?.role,
				workStations: atom?.workStation,
				employees: atom?.employee,
			},
		});
		const options = response.data.map((it: { name: string; id: number }) => ({
			label: it.name,
			value: it.name,
		}));
		return options;
	};

	const filters: Filter[] = [
		{
			key: 'timeRange',
			name: 'Período',
			options: [],
			isMulti: true,
			asyncFn: undefined,
			isDisabled: () => false,
		},
		{
			key: 'team',
			name: 'Turma',
			options: [],
			isMulti: true,
			asyncFn: (search, atom) => fetchTeamFilter(search, atom),
			isDisabled: () => false,
		},
		{
			key: 'management',
			name: 'Gerência',
			options: [],
			isMulti: true,
			asyncFn: (search, atom) =>
				fetchFilter('training-plannings/management', search, atom),
			isDisabled: () => false,
		},

		{
			key: 'areaCoordinator',
			name: 'Coordenação',
			options: [],
			isMulti: true,
			asyncFn: (search, atom) =>
				fetchFilter('training-plannings/area-coordinator', search, atom),
			isDisabled: () => false,
		},
		{
			key: 'role',
			name: 'Função',
			options: [],
			isMulti: true,
			asyncFn: (search, atom) =>
				fetchFilter('training-plannings/roles', search, atom),
			isDisabled: () => false,
		},
		{
			key: 'workStation',
			name: 'Local de Trabalho',
			options: [],
			isMulti: true,
			asyncFn: (search, atom) =>
				fetchFilter('training-plannings/work-station', search, atom),
			isDisabled: () => false,
		},

		{
			key: 'employee',
			name: 'Colaborador',
			options: [],
			isMulti: true,
			asyncFn: (search, atom) =>
				fetchFilter('training-plannings/employees', search, atom),
			isDisabled: () => false,
		},
	];
	const handleOpenNewTrainingPlanning = () => {
		navigate(`/home/ficheiros/${trainingFileId}/planejamento-de-turma`);
	};

	return (
		<div className="relative">
			<SideFilter
				refetchOnChange
				filters={filters}
				atom={trainingFilesDetailsFiltersAtom}
				applyChanges={handleApplyFilter}
			/>
			<div className="absolute -top-10 right-20 flex items-center">
				<Button
					className="mx-5 my-2 h-7  rounded-full px-5 text-xs font-medium text-white"
					onClick={handleOpenNewTrainingPlanning}
				>
					Adicionar Novo Planejamento
				</Button>
			</div>
			<div className="flex flex-col gap-2 py-3">
				<div className="flex gap-5 overflow-x-auto">
					<div className="w-48 rounded-md border-2 border-gray-200 bg-white px-4 py-3">
						<p className="text-xs">Treinamentos em folga</p>
						<h4 className="text-3xl font-bold text-primary">
							{data?.dayOffTrainingCount ?? 0}
						</h4>
					</div>
					<div className="w-48 rounded-md border-2 border-gray-200 bg-white px-4 py-3">
						<p className="text-xs">Transportes solicitados</p>
						<h4 className="text-3xl font-bold text-primary">
							{data?.requestedTransportCount ?? 0}
						</h4>
					</div>
					<div className="w-48 rounded-md border-2 border-gray-200 bg-white px-4 py-3">
						<p className="text-xs">Dias de alojamento</p>
						<h4 className="text-3xl font-bold text-primary">
							{data?.extraAccommodationCount ?? 0}
						</h4>
					</div>
					<div className="w-48 rounded-md border-2 border-gray-200 bg-white px-4 py-3">
						<p className="text-xs">Refeições extra</p>
						<h4 className="text-3xl font-bold text-primary">
							{data?.extraMealCount ?? 0}
						</h4>
					</div>
					<div className="w-48 rounded-md border-2 border-gray-200 bg-white px-4 py-3">
						<p className="text-xs">Aprovações pendentes</p>
						<h4 className="text-3xl font-bold text-primary">
							{data?.pendingApprovalCount ?? 0}
						</h4>
					</div>
					<div className="w-48 rounded-md border-2 border-gray-200 bg-white px-4 py-3">
						<p className="text-xs">Confirmações pendentes</p>
						<h4 className="text-3xl font-bold text-primary">
							{data?.pendingConfirmationCount ?? 0}
						</h4>
					</div>
				</div>
				<div>
					{isLoading ? (
						<div
							className="flex h-full w-full items-center justify-center"
							key="loading-1"
						>
							<Spinner size={50} />
						</div>
					) : data?.teams.length ? (
						<div className="flex flex-col gap-4">
							{data?.teams.map((planningTeam) => {
								return (
									<TrainingFileTeamCard
										trainingFileTeam={planningTeam}
										concluded={
											data.trainingFile.status ===
												trainingFileStatus.CONCLUDED ||
											data.trainingFile.status === trainingFileStatus.FINISHED
										}
										key={planningTeam.id}
										defaultOpen={!!paramTeam}
									/>
								);
							})}
						</div>
					) : (
						<div className="my-4 flex h-full min-h-[50vh] w-full flex-col items-center justify-center rounded-md bg-white">
							<img
								src={EmptyIcon}
								className="w-40"
								alt="Nenhuma turma encontrada"
							/>
							<div className="w-full text-center text-sm">
								<p>Nenhuma turma encontrada com esses parâmetros</p>
								<span className="font-semibold text-blue-800">
									Cadastre um ou altere os valores dos filtros para visualizar!
								</span>
							</div>
						</div>
					)}
					{}
				</div>
			</div>
		</div>
	);
};

export default TrainingFileTeams;
